import { useEffect, useRef, useState } from "react";

import { useEvmBackend } from "../context";

/** Default poll interval in ms. */
const DEFAULT_INTERVAL_MS = 4000;

/** Module-level block number cache to deduplicate concurrent getBlockNumber calls. */
let cachedBlock: { blockNumber: bigint; timestamp: number } | null = null;
const CACHE_TTL_MS = 1000;

async function getCachedBlockNumber(publicClient: {
  getBlockNumber: () => Promise<bigint>;
}): Promise<bigint> {
  const now = Date.now();
  if (cachedBlock && now - cachedBlock.timestamp < CACHE_TTL_MS) {
    return cachedBlock.blockNumber;
  }
  const bn = await publicClient.getBlockNumber();
  cachedBlock = { blockNumber: bn, timestamp: now };
  return bn;
}

/**
 * Subscribe to new blocks via polling and optionally run a callback on each.
 * Backend-agnostic: uses publicClient.getBlockNumber() from context.
 */
export function useOnBlock(
  callback?: (blockNumber: bigint) => void | Promise<void>,
  options?: { interval?: number },
): bigint | undefined {
  const { publicClient } = useEvmBackend();
  const [blockNumber, setBlockNumber] = useState<bigint | undefined>();

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const interval = options?.interval ?? DEFAULT_INTERVAL_MS;

  useEffect(() => {
    let cancelled = false;
    const timer: ReturnType<typeof setInterval> = setInterval(
      () => void poll(),
      interval,
    );

    async function poll() {
      try {
        const bn = await getCachedBlockNumber(publicClient);
        if (cancelled) return;
        setBlockNumber((prev) => {
          if (prev !== bn) {
            void callbackRef.current?.(bn);
          }
          return bn;
        });
      } catch {
        // ignore polling errors
      }
    }

    void poll();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [publicClient, interval]);

  return blockNumber;
}
